import { MenuItem } from '@chronosphereio/chrono-ui';
import { useState } from 'react';
import { VizPanelInspector } from '@/plugin-system/components/VizPanel/VizPanelInspector';

export interface UseVizContainerPanelInspectorProps {
  /**
   * Content to render when the panel inspector is open. Leave undefined to disable the panel inspector.
   */
  panelInspectorContent?: React.ReactNode;
}

export interface UseVizContainerPanelInspectorResult {
  /**
   * React node for rendering a menu item to trigger the panel inspector.
   */
  menuItem?: React.ReactNode;
  /**
   * React node for rendering the panel inspector.
   */
  panelInspector?: React.ReactNode;
}

const DISABLED_PANEL_INSPECTOR_RESULT: UseVizContainerPanelInspectorResult = {};

/**
 * Hook that manages the uncontrolled panel inspector baked into `VizContainer.Root`. Consumers can do their own
 * thing with the flexibility of composition if they want a controlled version.
 */
export const useVizContainerPanelInspector = ({
  panelInspectorContent,
}: UseVizContainerPanelInspectorProps): UseVizContainerPanelInspectorResult => {
  const [isOpen, setIsOpen] = useState(false);

  // The presence of panel inspector content gates if it is enabled or not.
  if (!panelInspectorContent) {
    // Panel inspector is not enabled, so return empty result.
    return DISABLED_PANEL_INSPECTOR_RESULT;
  }

  const menuItem = (
    <MenuItem key="panel-inspector" onClick={() => setIsOpen(true)}>
      Inspect panel
    </MenuItem>
  );

  const panelInspector = (
    <VizPanelInspector isOpen={isOpen} onClose={() => setIsOpen(false)}>
      {/* Only render the contents of the panel inspector when it's open because they can be 
            expensive. */}
      {isOpen && panelInspectorContent}
    </VizPanelInspector>
  );

  return {
    menuItem,
    panelInspector,
  };
};
